'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Script from 'next/script';
import {
  ReferFriendsAuthenticate,
  ReferFriendsContextValue,
  ReferFriendsProviderProps,
  ReferFriendsTrackCustomer
} from './types';
import { useIsEnabled } from '../../../shared/hooks/useIsEnabled';
import { Feature } from '../../../shared/utils/featureToggle';
import { FRIENDBUY_MERCHANT_ID, referFriendsButtonIdSchema } from './constants';
import { useStoreValue } from '../../../state/hooks/useStore';
import { getReferFriendBoardId } from './utils/getReferFriendBoardId';
import { useLocale } from '../../../shared/hooks/useLocale';
import {
  getAgReferFriendsBannerClosed,
  setAgReferFriendsBannerClosed
} from '../../../state/cookies/agReferFriendsBanner';
import { storeCodeSchema } from '../../../schema/storeCode';
import { useMutationReferFriend } from '../../../state/internal/referFriend/referFriend';

const { eu, uk, us, qa, ukd, eud } = storeCodeSchema.enum;

const ReferFriendsContext = createContext<ReferFriendsContextValue | null>(null);

const ReferFriendsProvider = ({ children }: ReferFriendsProviderProps) => {
  const isReferFriendsEnabled = useIsEnabled(Feature.referFriends);
  const store = useStoreValue();
  const locale = useLocale();
  const merchantId = FRIENDBUY_MERCHANT_ID[store];

  // Load states
  const [{ isFriendbuyLoaded, isCampaignsLoaded }, setIsLoaded] = useState({
    isFriendbuyLoaded: false,
    isCampaignsLoaded: false
  });
  const isLoaded = isFriendbuyLoaded && isCampaignsLoaded;

  const [showBanner, setShowBanner] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Gate the network: only load scripts when we actually need them.
  const [shouldLoad, setShouldLoad] = useState(false);

  // Avoid pushing 'merchant' more than once if props/state change.
  const merchantPushedRef = useRef(false);

  const { mutate: authenticate } = useMutationReferFriend();

  const isParticipatingStore = useMemo(
    () => [eu, uk, us, qa, ukd, eud].includes(store as any),
    [store]
  );

  // Queue is cheap; network is not. Prepare the queue immediately.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.friendbuyAPI = window.friendbuyAPI || [];
    if (!merchantPushedRef.current && merchantId) {
      window.friendbuyAPI.push(['merchant', merchantId]);
      merchantPushedRef.current = true;
    }
  }, [merchantId]);

  // Decide whether to show banner & trigger script load.
  useEffect(() => {
    const isBannerClosed = getAgReferFriendsBannerClosed();
    if (isParticipatingStore && !isBannerClosed) {
      setShowBanner(true);
      // Only load scripts when the banner is actually visible
      setShouldLoad(true);
    }
  }, [isParticipatingStore]);

  // Public API: allow any button/link to kick off Friendbuy on demand.
  const initReferFriends = useCallback(() => setShouldLoad(true), []);

  const handleTrackCustomer = useCallback<ReferFriendsTrackCustomer>(({ id, email }) => {
    window.friendbuyAPI.push(['track', 'customer', { id, email }]);
  }, []);

  const handleAuthenticate = useCallback<ReferFriendsAuthenticate>(
    (args) => {
      authenticate(args, {
        onSuccess: ({ authString, signature }) => {
          window.friendbuyAPI.push(['auth', authString, signature]);
          setIsAuthenticated(true);
        }
      });
    },
    [authenticate]
  );

  const handleCloseBanner = useCallback(() => {
    setAgReferFriendsBannerClosed(true);
    setShowBanner(false);
  }, []);

  const value = useMemo<ReferFriendsContextValue>(
    () => ({
      isLoaded,
      getBoardId: (isPortal: boolean) => getReferFriendBoardId({ isPortal, store, locale }),
      elementId: referFriendsButtonIdSchema.enum.friendbuyoverlay,
      showBanner,
      isAuthenticated,
      initReferFriends, // <-- expose to trigger loading on user interaction
      handleTrackCustomer: isLoaded ? handleTrackCustomer : null,
      handleAuthenticate: isLoaded ? handleAuthenticate : null,
      handleCloseBanner
    }),
    [
      handleAuthenticate,
      handleCloseBanner,
      handleTrackCustomer,
      isAuthenticated,
      isLoaded,
      locale,
      showBanner,
      store,
      initReferFriends
    ]
  );

  return (
    <ReferFriendsContext.Provider value={value}>
      {children}

      {/* Only mount scripts when we’ve decided we actually need Friendbuy */}
      {merchantId && isReferFriendsEnabled && shouldLoad && (
        <>
          {/* Loader is light; okay to run after hydration */}
          <Script
            id="friendbuy-core"
            src="https://static.fbot.me/friendbuy.js"
            strategy="afterInteractive"
            onReady={() => setIsLoaded((p) => ({ ...p, isFriendbuyLoaded: true }))}
          />

          <Script
            id="friendbuy-campaigns"
            src={`https://campaign.fbot.me/${merchantId}/campaigns.js`}
            strategy="lazyOnload"
            onReady={() => setIsLoaded((p) => ({ ...p, isCampaignsLoaded: true }))}
          />
        </>
      )}
    </ReferFriendsContext.Provider>
  );
};

function useReferFriends() {
  const context = useContext(ReferFriendsContext);
  if (!context) {
    throw new Error('useReferFriends must be used within a ReferFriendsProvider');
  }
  return context;
}

export default ReferFriendsContext;
export { ReferFriendsProvider, useReferFriends };
